// The versions routes over the API (E31-S1-T4, E31-S2-T3): a title's list, one version in full, and the
// review acts. Every act is one POST; its answer is the version as it now stands and goes straight into
// the cache, and the title's list is fetched again so its row speaks for the new state. A 409 means the
// version moved under the reviewer, so both are fetched again and the screen redraws from what is true.
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { ConflictError } from "../api/errors.js";
import { useApi } from "../api/provider.js";
import { PLAN_PATH, PlanRead } from "../pages/content/plan-model.js";
import { z } from "zod";
import { Approved, VersionDetail, VersionList, versionPath, versionsPath } from "./model.js";

/** GET /v1/production-plan, under the picker's key, so the two screens share one answer. */
export function usePlan() {
  const api = useApi();
  return useQuery({ queryKey: ["production-plan"], queryFn: () => api.get(PLAN_PATH, PlanRead) });
}

export const versionsKey = (titleId: string) => ["versions", "title", titleId] as const;
export const versionKey = (id: string) => ["versions", "version", id] as const;

export function useVersionList(titleId: string) {
  const api = useApi();
  return useQuery({
    queryKey: versionsKey(titleId),
    queryFn: () => api.get(versionsPath(titleId), VersionList),
  });
}

/** Every listed title's versions at once, for the scripts list. */
export function useVersionLists(titleIds: readonly string[]) {
  const api = useApi();
  return useQueries({
    queries: titleIds.map((titleId) => ({
      queryKey: versionsKey(titleId),
      queryFn: () => api.get(versionsPath(titleId), VersionList),
    })),
  });
}

export function useVersion(id: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: versionKey(id ?? ""),
    queryFn: () => api.get(versionPath(id ?? ""), VersionDetail),
    enabled: id !== undefined,
  });
}

export type ReviewAct =
  | { readonly act: "approve"; readonly teachesChanged: boolean }
  | { readonly act: "send-back"; readonly note: string }
  | { readonly act: "publish" };

export interface Acted {
  readonly act: ReviewAct["act"];
  readonly version: VersionDetail;
  /** Approve only: learners whose attestation the publish would expire (D-76). */
  readonly attestationsExpiring?: number;
}

export function useReviewAct(versionId: string, titleId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: versionsKey(titleId) });
    // The plan's summary counts a rendered version's measured runtime (ADR-032).
    void queryClient.invalidateQueries({ queryKey: ["production-plan"] });
  };
  return useMutation({
    mutationFn: async (act: ReviewAct): Promise<Acted> => {
      const path = `${versionPath(versionId)}/${act.act}`;
      switch (act.act) {
        case "approve": {
          const answer = await api.post(path, Approved, { teachesChanged: act.teachesChanged });
          return {
            act: act.act,
            version: answer.version,
            attestationsExpiring: answer.attestationsExpiring,
          };
        }
        case "send-back":
          return { act: act.act, version: await api.post(path, VersionDetail, { note: act.note }) };
        case "publish":
          return { act: act.act, version: await api.post(path, VersionDetail, {}) };
      }
    },
    onSuccess: ({ version }) => {
      queryClient.setQueryData(versionKey(versionId), version);
      refresh();
    },
    onError: (err) => {
      if (err instanceof ConflictError) {
        void queryClient.invalidateQueries({ queryKey: versionKey(versionId) });
        refresh();
      }
    },
  });
}

/** POST /v1/content/versions/:id/render: the 202 (ids and one flag; nothing of the script). */
const RenderRequested = z.object({ alreadyRequested: z.boolean() });

/**
 * Asks for the render of an approved version again, after a failed one. The 202 carries no version, so
 * the version and its title's list are fetched again; the screen then draws it as rendering. A 409-like
 * move under the reviewer is the same story: fetch again.
 */
export function useRequestRender(versionId: string, titleId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: versionKey(versionId) }),
      queryClient.invalidateQueries({ queryKey: versionsKey(titleId) }),
      queryClient.invalidateQueries({ queryKey: ["production-plan"] }),
    ]);
  return useMutation({
    mutationFn: () => api.post(`${versionPath(versionId)}/render`, RenderRequested, {}),
    onSuccess: refresh,
  });
}
